import React, { useContext } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext'; // Importe o AppContext

interface FormValues {
  type: string;
}

const Type: React.FC = () => {
  const navigate = useNavigate();
  const context = useContext(AppContext);

  // Verifica se o contexto está definido
  if (!context) {
    throw new Error('Step2 must be used within an AppProvider');
  }

  const { appData, updateAppData } = context;

  const initialValues: FormValues = {
    type: appData.type || '',
  };

  const validationSchema = Yup.object({
    type: Yup.string().required('Escolha o tipo do app'),
  });

  const onSubmit = (values: FormValues) => {
    updateAppData({ ...appData, type: values.type }); // Atualiza appData.type
    navigate(`/${appData.id}/titulo`); // Ajuste o caminho conforme necessário
  };

  return (
    <div className="content">
      <p className="font-semibold tracking-tight text-pretty text-gray-900 sm:text-2xl lg:text-balance">
        Escolha o tipo de app do seu plano:
      </p>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values }) => (
          <Form>
            {/* Web Rádio */}
            <p className="mt-6 font-semibold text-lg/8">Web Rádio</p>
            <div className="flex space-x-6 mt-6">
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Field
                  type="radio"
                  name="type"
                  value="web-radio"
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <div className="ml-3">
                  <span className="block text-lg font-medium">Web Rádio</span>
                  <span className="block text-sm text-gray-500">App para rádio</span>
                </div>
              </label>
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Field
                  type="radio"
                  name="type"
                  value="web-radio-tv"
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <div className="ml-3">
                  <span className="block text-lg font-medium">Web Rádio + Câmera Estúdio</span>
                  <span className="block text-sm text-gray-500">App para rádio com TV para a câmera no estúdio</span>
                </div>
              </label>
            </div>

            {/* Web TV */}
            <p className="mt-6 font-semibold text-lg/8">Web TV</p>
            <div className="flex space-x-6 mt-6">
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Field
                  type="radio"
                  name="type"
                  value="web-tv"
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <div className="ml-3">
                  <span className="block text-lg font-medium">Web TV</span>
                  <span className="block text-sm text-gray-500">App para TV</span>
                </div>
              </label>
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Field
                  type="radio"
                  name="type"
                  value="web-tv-radio"
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <div className="ml-3">
                  <span className="block text-lg font-medium">Web TV + Rádio</span>
                  <span className="block text-sm text-gray-500">App para TV e Rádio juntos</span>
                </div>
              </label>
            </div>

            {/* Web Igreja */}
            <p className="mt-6 font-semibold text-lg/8">Web Igreja</p>
            <div className="flex space-x-6 mt-6">
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Field
                  type="radio"
                  name="type"
                  value="web-church"
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <div className="ml-3">
                  <span className="block text-lg font-medium">Web Igreja</span>
                  <span className="block text-sm text-gray-500">App para Igreja com transmissões do Youtube</span>
                </div>
              </label>
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Field
                  type="radio"
                  name="type"
                  value="web-church-tv"
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <div className="ml-3">
                  <span className="block text-lg font-medium">Web Igreja + TV</span>
                  <span className="block text-sm text-gray-500">App para Igreja com Web TV</span>
                </div>
              </label>
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Field
                  type="radio"
                  name="type"
                  value="web-church-radio"
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <div className="ml-3">
                  <span className="block text-lg font-medium">Web Igreja + Rádio</span>
                  <span className="block text-sm text-gray-500">App para Igreja com Web Rádio</span>
                </div>
              </label>
              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Field
                  type="radio"
                  name="type"
                  value="web-church-tv-radio"
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <div className="ml-3">
                  <span className="block text-lg font-medium">Web Igreja + TV + Rádio</span>
                  <span className="block text-sm text-gray-500">App para Igreja com Web TV e Rádio</span>
                </div>
              </label>
            </div>

            {/* Mensagem importante */}
            <p className="mt-6 text-lg/8 text-yellow-600 bg-yellow-100 p-4 rounded-md">
              <strong>Importante:</strong> Certifique-se de que o tipo escolhido é compatível com o plano contratado antes de prosseguir. Em caso de dúvidas, entre em contato conosco.
            </p>

            {/* Mensagem de erro */}
            <div className="flex justify-end">
              <ErrorMessage name="type" component="div" className="text-white mt-2 alert alert-error alert-soft" />
            </div>
            
            {/* Botões de navegação */}
            <div className="flex justify-between mt-6">
              <Link
                to={`/${appData.id}`}
                
                className="bg-primary text-white px-6 py-3 text-xl rounded-md flex items-center space-x-2"
              >
                <i className="fas fa-arrow-left"></i>
                <span>Início</span>
              </Link>
              <button
                type="submit"
                className="bg-primary text-white px-6 py-3 text-xl rounded-md flex items-center space-x-2"
              >
                <span>Título</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Type;